/*global chrome*/

import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { toast } from 'react-toastify'

import { baseURL } from '../config'
import posthogService from './posthogService'

export async function simpleLogin(user, source) {
	axios.post(`${baseURL}/api/users/login`, user).then(res => {
		const { token } = res.data

		console.log('simpleLogin source', source)

		if (source === 'extension') {
			window.location.href = `https://relay.amazon.com?token=${token}`

			window.opener.postMessage(
				{
					source: 'loadfetcherLogin',
					token: token,
				},
				'*'
			)

			localStorage.clear()

			document.cookie.split(';').forEach(c => {
				document.cookie = c
					.replace(/^ +/, '')
					.replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
			})
			window.close()
		} else {
			token && localStorage.setItem('jwtToken', token)
			user.email && localStorage.setItem('email', user.email)
			window.location = '/loadboard'
		}
	})
}

export async function register(user) {
	const res = await axios.post(baseURL + '/api/users/register', user)
	return res.data
}

const sendTokenToChromeExtension = (extensionId, token) => {
	if (chrome && chrome.runtime && chrome.runtime.sendMessage) {
		chrome.runtime.sendMessage(extensionId, token)
	} else {
		console.log('Chrome extension messaging API not available.')
	}
}

export async function login({
	user,
	location,
	navigate,
	sourceExtension,
	windowOpender,
	toPayments = false,
	redirectUri,
	userToken,
}) {
	const res = await axios
		.post(`${baseURL}/api/users/login`, {
			...user,
			source: sourceExtension,
			userToken,
			token: userToken,
		})
		.catch(err => {
			if (err.response && err.response.status === 403) {
				toast.error('You signed up with Google. Log in with Google!', {
					position: 'top-center',
					autoClose: 8000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
				})
			}
			throw err
		})

	// Check if 2FA is required
	if (res.data?.requiresTwoFactor) {
		return {
			requiresTwoFactor: true,
			pendingLoginToken: res.data.pendingLoginToken,
			maskedEmail: res.data.maskedEmail,
		}
	}

	const token = res.data?.token
	const extensionToken = res.data?.extensionToken

	if (token) localStorage.setItem('jwtToken', token)

	const productionExtensionId = 'ihcgicdogclbieclnldfjmlglkolankb'
	const extensionId = productionExtensionId

	if (extensionToken) {
		sendTokenToChromeExtension(extensionId, { extensionToken })
	}

	const params = new URLSearchParams(location.search)

	if (sourceExtension && windowOpender) {
		if (!extensionToken) return
		windowOpender?.postMessage(
			{ source: 'loadfetcherLogin', token: extensionToken },
			'*'
		)
		window.close()
	} else if (params.get('source') === 'extension') {
		window.location.href = `https://relay.amazon.com?token=${token}`
		window.opener.postMessage(
			{ source: 'loadfetcherLogin', token: extensionToken },
			'*'
		)
		window.close()
	} else {
		token && localStorage.setItem('jwtToken', token)
		user.email && localStorage.setItem('email', user.email)
	}

	if (toPayments) return true

	if (redirectUri) {
		window.location = redirectUri
	} else {
		window.location = '/loadboard'
	}

	return setTimeout(() => {
		navigate('/loadboard')
	}, 2000)
}

export async function verifyTwoFactor({
	pendingLoginToken,
	otpCode,
	extensionOnly,
}) {
	const res = await axios.post(`${baseURL}/api/users/verify-two-factor`, {
		pendingLoginToken,
		otpCode,
		extensionOnly,
	})
	return res.data
}

export async function resendTwoFactor({ pendingLoginToken }) {
	const res = await axios.post(`${baseURL}/api/users/resend-two-factor`, {
		pendingLoginToken,
	})
	return res.data
}

/**
 * Passwordless entry. One endpoint covers both sign-up and sign-in, and the
 * response is identical either way, so nothing here reveals whether the address
 * already has an account.
 */
export async function requestEmailCode({ email, agreedToTerms = false }) {
	const res = await axios.post(`${baseURL}/api/auth/email-code`, {
		email,
		agreedToTerms,
	})
	return res.data
}

/**
 * Resolves to a session, or to { requiresTerms, consentToken } for a new address.
 * source/userToken are what earn an extension token back, so signing in from the
 * extension popup works the same way it does for password and passkey.
 */
export async function verifyEmailCode({
	pendingLoginToken,
	otpCode,
	sourceExtension,
	extensionOnly,
	userToken,
}) {
	const res = await axios.post(`${baseURL}/api/auth/email-verify`, {
		pendingLoginToken,
		otpCode,
		source: sourceExtension ? 'extension' : undefined,
		extensionOnly,
		token: userToken,
	})
	return res.data
}

export async function acceptEmailCodeTerms({
	consentToken,
	sourceExtension,
	extensionOnly,
	userToken,
}) {
	const res = await axios.post(`${baseURL}/api/auth/accept-terms`, {
		consentToken,
		source: sourceExtension ? 'extension' : undefined,
		extensionOnly,
		token: userToken,
	})
	return res.data
}

export function getCurrentUser() {
	try {
		const jwt = localStorage.getItem('jwtToken')

		if (!jwt) return null

		const user = jwtDecode(jwt)

		return user
	} catch (error) {
		return error
	}
}

export const requestPasswordReset = async (email, turnstileToken) => {
	const response = await axios.post(`${baseURL}/api/user/reset-password`, {
		email,
		'cf-turnstile-response': turnstileToken,
	})
	return response.data
}

export const resetPassword = async (token, newPassword, confirmPassword) => {
	const response = await axios.post(
		`${baseURL}/api/user/reset-password/${token}`,
		{ newPassword, confirmPassword }
	)
	return response.data
}

export function getAuthHeaders() {
	const token = localStorage.getItem('jwtToken')
	if (!token) {
		return {
			'Content-Type': 'application/json',
		}
	}

	const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`

	return {
		'Content-Type': 'application/json',
		Authorization: authToken,
	}
}

export function logout() {
	try {
		localStorage.removeItem('jwtToken')
		localStorage.removeItem('email')
		// Reset PostHog session on logout
		posthogService.reset()
	} catch (err) {}
}

export async function checkCurrentUser() {
	try {
		const { exp } = getCurrentUser()

		if (Date.now() >= exp * 1000) {
			logout()
			window.location = '/'
			return window.location.reload()
		}
	} catch (error) {}
}

// const upgradeUser = async (user, plan) => {}

export default {
	login,
	requestEmailCode,
	verifyEmailCode,
	acceptEmailCodeTerms,
	getCurrentUser,
	logout,
	checkCurrentUser,
	verifyTwoFactor,
	resendTwoFactor,
}
